// import node_modules
import * as PropTypes from 'prop-types';
import * as React from 'react';

// import types
import type { MDXRemoteSerializeResult } from 'next-mdx-remote';
import type { GetStaticProps, GetStaticPaths } from 'next';

// import modules
import { fetchBlogTags, fetchBlogPosts } from '../../../modules/fetcher';

// import component
import BlogTagTemplate from '../../../components/templates/BlogTagTemplate';

// define page
const BlogTag: React.FunctionComponent<{
  blogTag: { content: MDXRemoteSerializeResult; slug: string; title: string };
  blogTags: { slug: string; title: string }[];
  blogPosts: { image: string; slug: string; tags: string[]; title: string }[];
  hrefLang: string;
}> = ({ blogTag, blogTags, blogPosts, hrefLang }) => {
  return (
    <BlogTagTemplate
      blogTag={blogTag}
      blogTags={blogTags}
      blogPosts={blogPosts}
      hrefLang={hrefLang}
    />
  );
};

BlogTag.propTypes = {
  blogPosts: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      tags: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      title: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  blogTag: PropTypes.shape({
    content: PropTypes.shape({ compiledSource: PropTypes.string.isRequired }).isRequired,
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  blogTags: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  hrefLang: PropTypes.string.isRequired,
};

export const getStaticPaths: GetStaticPaths = async () => {
  // init vars
  const locale = 'en';

  // fetch tags
  const blogTags = await fetchBlogTags();
  const blogTagsLang = blogTags.filter((item) => item.locale === locale);

  // create paths
  const paths = blogTagsLang.map((item) => ({ params: { tag: item.tag.slug } }));

  // return params
  return { paths: paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async (context) => {
  // get vars
  const locale = 'en';
  const { tag } = context?.params || {};

  // fetch tags
  const blogTagsLang = await fetchBlogTags();
  const blogTags =
    blogTagsLang?.filter((item) => item.locale === locale).map((item) => item.tag) || [];
  const blogTag = blogTags?.find((item) => item?.slug === tag);

  // check blog post
  if (!blogTag) {
    return {
      notFound: true,
    };
  }

  // fetch posts
  const blogPostsLang = await fetchBlogPosts({
    filter: { tag: tag as string },
    sort: { key: 'createdAt', direction: 'desc' },
  });
  const blogPosts = blogPostsLang.filter((item) => item.locale === locale).map((item) => item.post);

  // return data
  return { props: { blogTag, blogPosts, blogTags, hrefLang: blogTag.hrefLang } };
};

export default BlogTag;
